/**
 * EventBus - Observer Pattern Implementation
 * 
 * Decouples event producers (collectors) from consumers (transport, middleware).
 * Collectors emit events without knowing who will handle them.
 */

export type EventHandler<T = any> = (data: T) => void | Promise<void>;

interface Subscription {
  handler: EventHandler;
  once: boolean;
}

export class EventBus {
  private subscriptions: Map<string, Set<Subscription>>;
  private debug: boolean;

  constructor(debug = false) {
    this.subscriptions = new Map();
    this.debug = debug;
  }

  /**
   * Subscribe to an event type
   */
  subscribe<T = any>(eventType: string, handler: EventHandler<T>): () => void {
    if (!this.subscriptions.has(eventType)) {
      this.subscriptions.set(eventType, new Set());
    }

    const subscription: Subscription = { handler, once: false };
    this.subscriptions.get(eventType)!.add(subscription);

    this.log(`Subscribed to ${eventType}`);

    // Return unsubscribe function
    return () => {
      this.subscriptions.get(eventType)?.delete(subscription);
      this.log(`Unsubscribed from ${eventType}`);
    };
  }

  /**
   * Subscribe to an event type only once
   */
  once<T = any>(eventType: string, handler: EventHandler<T>): () => void {
    if (!this.subscriptions.has(eventType)) {
      this.subscriptions.set(eventType, new Set());
    }

    const subscription: Subscription = { handler, once: true };
    this.subscriptions.get(eventType)!.add(subscription);

    this.log(`Subscribed once to ${eventType}`);

    // Return unsubscribe function
    return () => {
      this.subscriptions.get(eventType)?.delete(subscription);
    };
  }

  /**
   * Emit an event to all subscribers
   */
  async emit<T = any>(eventType: string, data: T): Promise<void> {
    const subscriptions = this.subscriptions.get(eventType);
    
    if (!subscriptions || subscriptions.size === 0) {
      this.log(`No subscribers for ${eventType}`);
      return;
    }

    this.log(`Emitting ${eventType} to ${subscriptions.size} subscriber(s)`);

    // Execute all handlers
    const promises: Promise<void>[] = [];
    const toRemove: Subscription[] = [];

    // Convert Set to Array for iteration
    const subscriptionsArray = Array.from(subscriptions);
    
    for (const subscription of subscriptionsArray) {
      try {
        const result = subscription.handler(data);
        if (result instanceof Promise) {
          promises.push(result);
        }

        // Mark for removal if it was a once subscription
        if (subscription.once) {
          toRemove.push(subscription);
        }
      } catch (error) {
        console.error(`[EventBus] Error in handler for ${eventType}:`, error);
      }
    }

    // Remove once subscriptions
    toRemove.forEach(sub => subscriptions.delete(sub));

    // Wait for all async handlers
    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }

  /**
   * Emit an event synchronously (fire and forget, don't wait for async handlers)
   */
  emitSync<T = any>(eventType: string, data: T): void {
    const subscriptions = this.subscriptions.get(eventType);
    
    if (!subscriptions || subscriptions.size === 0) {
      return;
    }

    const toRemove: Subscription[] = [];
    
    // Convert Set to Array for iteration
    const subscriptionsArray = Array.from(subscriptions);

    for (const subscription of subscriptionsArray) {
      try {
        subscription.handler(data);

        if (subscription.once) {
          toRemove.push(subscription);
        }
      } catch (error) {
        console.error(`[EventBus] Error in handler for ${eventType}:`, error);
      }
    }

    // Remove once subscriptions
    toRemove.forEach(sub => subscriptions.delete(sub));
  }

  /**
   * Unsubscribe all handlers for an event type
   */
  unsubscribeAll(eventType: string): void {
    this.subscriptions.delete(eventType);
    this.log(`Unsubscribed all from ${eventType}`);
  }

  /**
   * Clear all subscriptions
   */
  clear(): void {
    this.subscriptions.clear();
    this.log('Cleared all subscriptions');
  }

  /**
   * Get count of subscriptions for an event type
   */
  getSubscriptionCount(eventType: string): number {
    return this.subscriptions.get(eventType)?.size || 0;
  }

  /**
   * Get all event types that have subscriptions
   */
  getEventTypes(): string[] {
    return Array.from(this.subscriptions.keys());
  }

  private log(message: string): void {
    if (this.debug) {
      console.log(`[EventBus] ${message}`);
    }
  }
}

